import { setGlobalWindowVariable } from 'utils/globalWindowVariables';
import { Store } from 'redux';
import State from 'store/State';
import { LoggingService } from '@instinet-analytics/core-lib';
import { buildLogger, Logger } from '@instinet-analytics/core-lib';
import { embeddedApiSendMessageAction } from 'embedded/api/store/actions';
import { Identity, identitySchema } from 'embedded/api/out/Identity';
import { OutTopic } from 'embedded/api/out/OutTopic';
import { Listener } from 'embedded/api/out/Listener';
import { OutMessage, OutMessagePayload } from 'embedded/api/out/OutMessage';
import { bootstrapOutTopics } from 'embedded/api/out/bootstrapper';
import { AnyOutMessageTopic } from 'embedded/api/out/OutMessageTopic';
import { EmbeddedApiServiceOptions } from 'embedded/api/EmbeddedApiService';
import { AjvValidator } from 'utils/jsonValidation/AjvValidator';
import { SubscriberInfo } from 'embedded/api/out/Subscription';
import { WindowsCefQuerySubscriberFactory } from 'embedded/api/out/WindowsCefQuerySubscriber';

const identityValidator = new AjvValidator<Identity>(identitySchema);

interface PublishResult {
    success: boolean;
    error?: Error;
}

export class OutMessagesProcessor {
    private readonly outTopics: Record<OutTopic, AnyOutMessageTopic>;

    private readonly logger: Logger;
    private readonly loggingService: LoggingService;
    private cefQuerySubscriberFactory = new WindowsCefQuerySubscriberFactory();

    public store: Store<State> | undefined;

    public constructor(options: EmbeddedApiServiceOptions) {
        const { loggingService } = options;
        this.logger = buildLogger('EmbeddedApiService', loggingService);
        this.loggingService = loggingService;
        this.outTopics = bootstrapOutTopics();

        setGlobalWindowVariable({
            subscribe: (identity, topic, listener) =>
                this.subscribe(identity, topic as OutTopic, listener),
            unsubscribe: (identity, topic) =>
                this.unsubscribe(identity, topic as OutTopic),
            publish: (topic, payload) => {
                const message: OutMessage<OutTopic, OutMessagePayload> = {
                    topic: topic as OutTopic,
                    payload: payload as unknown as OutMessagePayload,
                };
                this.publish(message);
            },
        });
    }

    public async subscribe<TPayload>(
        identity: Identity,
        topic: OutTopic,
        listener: Listener<TPayload>
    ): Promise<string> {
        if (!this.store) {
            throw new Error('Store has not been set');
        }

        const { outTopic, error } = this.assertValidSubscription(
            identity,
            topic
        );

        if (error) {
            return Promise.reject(error);
        }

        const { subscriptions, publishOnSubscribe } = outTopic!;

        // Remove previous subscriptions for this identity...
        await this.unsubscribe(identity, topic);

        // Subscribe...
        subscriptions.push({ identity, listener });

        // Immediate publish if configured...
        if (publishOnSubscribe) {
            setTimeout(() => {
                publishOnSubscribe(identity, listener, this.store!);
            }, 0);
        }

        return Promise.resolve(`Successfully subscribed to ${topic}`);
    }

    public hasSubscriber(topic: OutTopic): boolean {
        return this.getTopicSubscribers(topic).length > 0;
    }

    public unsubscribe(identity: Identity, topic: OutTopic): Promise<string> {
        const { error, outTopic } = this.assertValidSubscription(
            identity,
            topic
        );

        if (error) {
            return Promise.reject(error);
        }

        const subscriptions = outTopic!.subscriptions;

        const subscription = subscriptions!.find(
            (s) => s.identity.uuid === identity.uuid
        );
        if (!subscription) {
            return Promise.resolve(
                `No listener to unsubscribe for ${identity.uuid} to ${topic}`
            );
        }

        const index = subscriptions.indexOf(subscription);
        subscriptions.splice(index, 1);

        return Promise.resolve(
            `Listener ${identity.uuid} unsubscribed from ${topic}`
        );
    }

    public publish<T extends OutTopic, TPayload>(
        message: OutMessage<T, TPayload>
    ): PublishResult {
        const subscribers = this.getTopicSubscribers(message.topic);

        let result: PublishResult = { success: true };
        for (const subscriber of subscribers) {
            try {
                subscriber.listener(message.payload);

                this.store!.dispatch(
                    embeddedApiSendMessageAction(
                        subscriber.identity,
                        message.topic,
                        'Success',
                        'Message Sent',
                        message.payload
                    )
                );
            } catch (error) {
                this.store!.dispatch(
                    embeddedApiSendMessageAction(
                        subscriber.identity,
                        message.topic,
                        'Error',
                        'Exception from subscriber',
                        message.payload,
                        error as Error
                    )
                );
                this.logger.critical(`Error sending message`, {
                    message,
                    error,
                    subscriberIdentity: subscriber.identity,
                });
                result = { success: false, error: error as Error };
            }
        }
        return result;
    }

    private assertValidSubscription(
        identity: Identity,
        topic: OutTopic
    ): {
        error?: { type: string; message: string };
        outTopic?: AnyOutMessageTopic;
    } {
        const outTopic = this.outTopics[topic];
        if (!outTopic) {
            this.logger.warn(`Invalid topic '${topic}' received`, {
                identity,
            });
            this.store!.dispatch(
                embeddedApiSendMessageAction(
                    identity,
                    topic,
                    'Error',
                    'Invalid Topic'
                )
            );

            return {
                error: {
                    type: 'Invalid Topic',
                    message: `Invalid topic '${topic}' received`,
                },
            };
        }

        const validationResult = identityValidator.validate(identity);
        if (!validationResult.success) {
            this.store!.dispatch(
                embeddedApiSendMessageAction(
                    identity,
                    topic,
                    'Error',
                    'Invalid Identity'
                )
            );

            return {
                error: {
                    type: 'Invalid Identity',
                    message: validationResult.error,
                },
            };
        }
        return { outTopic };
    }

    public getSubscribersInfo(): SubscriberInfo[] {
        const ret: SubscriberInfo[] = [];
        for (const topicName of Object.keys(this.outTopics)) {
            const topic = this.getTopicSubscribers(topicName as OutTopic);
            for (const subscription of topic) {
                ret.push({ ...subscription.identity, topic: topicName });
            }
        }

        return ret;
    }

    private getTopicSubscribers(topic: OutTopic) {
        const ret = [...this.outTopics[topic].subscriptions];
        const cefQuerySubscriber = this.cefQuerySubscriberFactory.getSubscriber(
            topic,
            this.loggingService
        );
        if (cefQuerySubscriber) {
            ret.push(cefQuerySubscriber);
        }
        return ret;
    }
}
